import { MutableRefObject } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { TimezoneKey } from "@impero-com/common/utils/timezone";
import moment from "moment-timezone";
import * as z from "zod";

import {
    InitialFieldType,
    SubsequentFieldType,
    ControlScheduling,
    DataSheetAssignmentsListDto,
} from "api/generated/model";
import { getAssignmentIdToSodIssues } from "components/controlAssignment/getAssignmentIdToSodIssues";
import { Field } from "components/fieldBuilder/models";
import {
    fileUploadFieldTypeSchema,
    getBaseFieldSchema,
    getCheckboxGroupFieldTypeSchema,
    getDataGridFieldTypeSchema,
    getDropdownFieldTypeSchema,
    getRadioButtonGroupFieldTypeSchema,
    headingFieldTypeSchema,
    moneyInputFieldTypeSchema,
    numericInputFieldTypeSchema,
    textAreaFieldTypeSchema,
    textInputFieldTypeSchema,
    validateFieldConditions,
} from "components/fieldBuilder/zodSchemas";
import { ControlAssignmentData } from "utils/controls/assignmentUtils";

import { getControlFieldTypeConditionalValues } from "./components/tasks/fieldBuilderUtils";
import {
    ControlViewFormState,
    toParsedControlScheduling,
    updateFromParsedControlScheduling,
    ParsedControlScheduling,
} from "./formData";
import { hoursToMicros, microsToHours } from "./utils/microsecondsToTime";
import { normalizeRecurrenceRule } from "./utils/normalizeRecurrenceRule";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export const getTitleSchema = (t: TFunction) =>
    z
        .string()
        .trim()
        .min(1, { error: t("Validation_Required") })
        .max(255);

export const descriptionSchema = z.string().trim();

// ---------------------------------------------------------------------------
// Scheduling rule schema — discriminated union with per-variant validation
// ---------------------------------------------------------------------------

const monthDueDateOffsetSchema = z.object({
    offsetPeriod: z.object({
        months: z.int().min(0).max(99),
        days: z.int().min(0).max(30),
    }),
    anchor: z.enum(["StartOfInterval", "EndOfInterval"]),
    offsetType: z.enum(["BeforeAnchor", "AfterAnchor"]),
});

const defaultOrCustomOffsetSchema = z.union([
    z.literal("Default"),
    z.object({ Custom: monthDueDateOffsetSchema }),
]);

/** Occurrences record — keys are period names (months, quarters, etc.), values are Default or Custom offset */
const occurrencesSchema = (t: TFunction) =>
    z
        .record(z.string(), defaultOrCustomOffsetSchema)
        .refine((v) => Object.keys(v).length > 0, {
            error: t("Recurrence_Validation_Invalid_Occurrences"),
        });

function getRecurrenceRuleSchema(t: TFunction) {
    const ruleWithOffset = z.object({
        defaultDueDateOffset: monthDueDateOffsetSchema,
        calendarId: z.int().nullable(),
        occurrences: occurrencesSchema(t),
    });

    const ruleWithOffsetAndFinancialYear = ruleWithOffset.extend({
        financialYearId: z.int().nullable(),
    });

    return z.discriminatedUnion("type", [
        z.object({ type: z.literal("Date") }),
        z.object({
            type: z.literal("Weekly"),
            days: z
                .array(
                    z.enum([
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                    ])
                )
                .min(1, t("Recurrence_Validation_Invalid_WeeklyDays")),
            periodicity: z.int().min(1).max(6),
        }),
        ruleWithOffset.extend({ type: z.literal("Monthly") }),
        ruleWithOffset.extend({ type: z.literal("EveryTwoMonths") }),
        ruleWithOffsetAndFinancialYear.extend({ type: z.literal("Quarterly") }),
        ruleWithOffsetAndFinancialYear.extend({
            type: z.literal("HalfYearly"),
        }),
        z.object({
            type: z.literal("Yearly"),
            defaultDueDateOffset: monthDueDateOffsetSchema,
            calendarId: z.int().nullable(),
            financialYearId: z.int().nullable(),
            periodicity: z.int().min(1),
        }),
    ]);
}

// ---------------------------------------------------------------------------
// Task field schema — validates the common Field<> structure
// ---------------------------------------------------------------------------

const MAX_HOURS = 23;
const MAX_DAYS = 999;

/** Reviewer allocated time: value max depends on unit (23 for hours, 999 for days) */
function getAllocatedTimeReviewerSchema(t: TFunction) {
    return z
        .object({
            value: z.int().min(0),
            unit: z.enum(["days", "hours"]),
        })
        .refine((v) => v.value <= (v.unit === "hours" ? MAX_HOURS : MAX_DAYS), {
            error: t("Validation_Required"),
            path: ["value"],
        });
}

const initialAssignmentKindSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("User"), userId: z.number() }),
    z.object({ type: z.literal("Group"), userGroupId: z.number() }),
    z.object({
        type: z.literal("Pool"),
        userGroupId: z.number(),
        defaultClaimantId: z.number().nullable(),
    }),
]);

const subsequentAssignmentKindSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("User"), userId: z.number() }),
    z.object({ type: z.literal("Pool"), userGroupId: z.number() }),
]);

const notificationRecipientSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("User"), userId: z.number() }),
    z.object({ type: z.literal("Group"), userGroupId: z.number() }),
]);

function getControlAssignmentSchema(t: TFunction, triggersControls: boolean) {
    return z
        .object({
            initialAssignment: z.record(
                z.string(),
                initialAssignmentKindSchema
            ),
            subsequentAssignments: z.array(
                z.record(z.string(), subsequentAssignmentKindSchema)
            ),
            tagIds: z.record(z.string(), z.array(z.string())),
            notificationRecipients: z.record(
                z.string(),
                z.record(z.string(), z.array(notificationRecipientSchema))
            ),
            entityIds: z.record(z.string(), z.number()),
            loginProtectionEnabled: z.boolean(),
            segregationOfDutiesEnabled: z.boolean(),
        })
        .superRefine((value, ctx) => {
            if (triggersControls) {
                const initialAssignments = Object.values(
                    value.initialAssignment
                );
                if (initialAssignments.length > 1) {
                    ctx.addIssue({
                        code: "custom",
                        message: t(
                            "CreateControl_Assignees_TooManyAssignmentsForTriggeringControl"
                        ),
                    });
                    return;
                }
                if (initialAssignments[0]?.type === "Group") {
                    ctx.addIssue({
                        code: "custom",
                        message: t(
                            "CreateControl_Assignees_GroupAssignmentForTriggeringControl"
                        ),
                    });
                    return;
                }
            }

            const sodResult = Object.values(
                getAssignmentIdToSodIssues(value)
            )[0];
            if (!sodResult) {
                return;
            }

            if (sodResult.defaultClaimantError) {
                ctx.addIssue({
                    code: "custom",
                    message: t(
                        "CreateControl_Assignees_Sod_DefaultClaimantShouldBeDifferentFromReviewers"
                    ),
                });
            } else if (sodResult.firstReviewerError) {
                ctx.addIssue({
                    code: "custom",
                    message: t(
                        "CreateControl_Assignees_Sod_ReviewerShouldBeDifferentFromResponsible"
                    ),
                });
            } else if (sodResult.secondReviewerError) {
                const msg =
                    sodResult.secondReviewerError ===
                    "SecondReviewerShouldBeDifferentFromResponsible"
                        ? t(
                              "CreateControl_Assignees_Sod_SecondReviewerShouldBeDifferentFromResponsible"
                          )
                        : t(
                              "CreateControl_Assignees_Sod_SecondReviewerShouldBeDifferentFromReviewer"
                          );
                ctx.addIssue({ code: "custom", message: msg });
            }
        });
}

// ---------------------------------------------------------------------------
// Task field schemas
// ---------------------------------------------------------------------------

function getDataSheetFieldTypeSchema(
    t: TFunction,
    dataSheetAssignmentsRef: MutableRefObject<DataSheetAssignmentsListDto>
) {
    return z
        .object({
            type: z.literal("DataSheet"),
            dataSheetId: z.guid({ error: t("FieldBuilder_Error_DataSheet") }),
            grouping: z.array(z.union([z.string(), z.number(), z.null()])),
            autoCompletion: z
                .object({
                    columnId: z.guid(),
                    range: z.object({ lower: z.number(), upper: z.number() }),
                })
                .nullish(),
        })
        .check((ctx) => {
            const { dataSheetId, grouping } = ctx.value;
            const assignments = dataSheetAssignmentsRef.current;
            const selectedAssignment = assignments.content.find(
                (dsa) => dsa.resource.dataSheetId === dataSheetId
            );
            const selectedDataSheet =
                assignments.linkedResources.dataSheets.find(
                    (ds) => ds.id === dataSheetId
                );
            if (!selectedAssignment || !selectedDataSheet) {
                ctx.issues.push({
                    code: "custom",
                    message: t("FieldBuilder_Error_DataSheet"),
                    path: ["dataSheetId"],
                    input: dataSheetId,
                });
                return;
            }
            // Note: it is not an error if the grouping no longer exists
            // XXX Should we check the data types though?
            const groupingColumns = selectedDataSheet.columns.filter(
                (col) => col.groupingColumn
            );
            if (
                grouping.length === 0 ||
                grouping.length !== groupingColumns.length
            ) {
                ctx.issues.push({
                    code: "custom",
                    message: t("FieldBuilder_Error_DataSheet_Grouping"),
                    path: ["grouping"],
                    input: grouping,
                });
            }
        });
}

function getCommonFieldTypes(t: TFunction) {
    return [
        getDataGridFieldTypeSchema(t),
        textInputFieldTypeSchema,
        textAreaFieldTypeSchema,
        numericInputFieldTypeSchema,
        moneyInputFieldTypeSchema,
        getRadioButtonGroupFieldTypeSchema(t),
        getCheckboxGroupFieldTypeSchema(t),
        getDropdownFieldTypeSchema(t),
        fileUploadFieldTypeSchema,
        headingFieldTypeSchema,
    ] as const;
}

function getResponsibleFieldSchema(
    t: TFunction,
    dataSheetAssignmentsRef: MutableRefObject<DataSheetAssignmentsListDto>
) {
    return z
        .object({
            fieldType: z.discriminatedUnion("type", [
                getDataSheetFieldTypeSchema(t, dataSheetAssignmentsRef),
                ...getCommonFieldTypes(t),
            ]),
        })
        .merge(getBaseFieldSchema(t));
}

function getReviewerFieldSchema(t: TFunction) {
    return z
        .object({
            fieldType: z.discriminatedUnion("type", [
                ...getCommonFieldTypes(t),
            ]),
        })
        .merge(getBaseFieldSchema(t));
}

function getTaskArraySchema<S extends z.ZodType>(fieldSchema: S, t: TFunction) {
    return z
        .array(fieldSchema)
        .check((ctx) =>
            validateFieldConditions(
                ctx.value as Field<unknown, string>[],
                ctx,
                t,
                (fieldType) =>
                    getControlFieldTypeConditionalValues(
                        fieldType as InitialFieldType | SubsequentFieldType
                    )
            )
        );
}

// ---------------------------------------------------------------------------
// Main form schema
// ---------------------------------------------------------------------------

export function useControlViewFormSchema(
    triggersControls: boolean,
    dataSheetAssignmentsRef: MutableRefObject<DataSheetAssignmentsListDto>
) {
    const { t } = useTranslation();

    return z.object({
        title: getTitleSchema(t),
        description: descriptionSchema,
        controlProgramId: z
            .number()
            .nullable()
            .refine((v): boolean => v !== null, {
                error: t("Validation_Required"),
            }),
        tagIds: z.array(z.string()),

        scheduling: z.object({
            timezoneName: z.string().min(1),
            dueTime: z.date(),
            startDate: z.date(),
            coveredPeriod: z.nullable(
                z.discriminatedUnion("type", [
                    z.object({ type: z.literal("Month"), data: z.number() }),
                    z.object({
                        type: z.literal("TwoMonths"),
                        data: z.number(),
                    }),
                    z.object({ type: z.literal("Quarter"), data: z.number() }),
                    z.object({ type: z.literal("Half"), data: z.number() }),
                    z.object({ type: z.literal("Year"), data: z.number() }),
                ])
            ),
            rule: getRecurrenceRuleSchema(t),
        }),

        allocatedTimes: z.object({
            responsibleDays: z.int().min(0).max(999),
            sendTime: z.date(),
            firstReviewer: getAllocatedTimeReviewerSchema(t),
            secondReviewer: getAllocatedTimeReviewerSchema(t),
        }),

        controlAssignment: getControlAssignmentSchema(t, triggersControls),

        responsibleTasks: getTaskArraySchema(
            getResponsibleFieldSchema(t, dataSheetAssignmentsRef),
            t
        ),
        firstReviewerTasks: getTaskArraySchema(getReviewerFieldSchema(t), t),
        secondReviewerTasks: getTaskArraySchema(getReviewerFieldSchema(t), t),

        remindersBefore: z.array(z.number()),
        remindersAfter: z.array(z.number()),

        sendImmediately: z.boolean(),
        triggeredBy: z
            .string()
            .nullable()
            .refine((v) => v === null || v.length > 0, {
                error: t("Validation_Required"),
            }),
    });
}

export type ControlViewFormSchema = z.infer<
    ReturnType<typeof useControlViewFormSchema>
>;

// ---------------------------------------------------------------------------
// Conversion: ControlViewFormState → RHF default values
// ---------------------------------------------------------------------------

export function toRHFDefaultValues(
    formState: ControlViewFormState
): ControlViewFormSchema {
    const parsed = toParsedControlScheduling(formState.scheduling);
    const allocatedTimes = toAllocatedTimesValues(formState);

    return {
        title: formState.title,
        description: formState.description ?? "",
        controlProgramId: formState.controlProgramId,
        tagIds: formState.tagIds,
        scheduling: {
            timezoneName: parsed.timezoneName,
            dueTime: parsed.dueTime.toDate(),
            startDate: parsed.startDate.toDate(),
            coveredPeriod: parsed.coveredPeriod ?? null,
            rule: normalizeRecurrenceRule(parsed.rule),
        },
        allocatedTimes,
        controlAssignment: toControlAssignmentDataFromFormState(formState),
        responsibleTasks: formState.stepGroup.initialStep.fieldGroup.fields,
        firstReviewerTasks:
            formState.stepGroup.subsequentSteps[0].fieldGroup.fields,
        secondReviewerTasks:
            formState.stepGroup.subsequentSteps[1].fieldGroup.fields,
        remindersBefore: parsed.remindersBefore,
        remindersAfter: parsed.remindersAfter,
        sendImmediately: formState.sendImmediately,
        triggeredBy: formState.triggeredBy,
    };
}

// ---------------------------------------------------------------------------
// Conversion: RHF values → ControlViewFormState (for submit)
// ---------------------------------------------------------------------------

export function rhfValuesToControlViewFormState(
    values: ControlViewFormSchema,
    baseFormState: ControlViewFormState
): ControlViewFormState {
    // Reconstruct ParsedControlScheduling from the flat fields
    const parsedScheduling: ParsedControlScheduling = {
        ...values.scheduling,
        timezoneName: values.scheduling.timezoneName as TimezoneKey,
        dueTime: moment(values.scheduling.dueTime),
        startDate: moment(values.scheduling.startDate),
        sendTime: moment(values.allocatedTimes.sendTime),
        remindersBefore: values.remindersBefore,
        remindersAfter: values.remindersAfter,
    };
    const scheduling: ControlScheduling = updateFromParsedControlScheduling(
        baseFormState.scheduling,
        parsedScheduling
    );
    // Patch sendTime from allocatedTimes
    scheduling.sendTime = moment(values.allocatedTimes.sendTime).format(
        "HH:mm"
    );

    // Reconstruct stepGroup
    const stepGroup = reconstructStepGroup(values, baseFormState);

    // Reconstruct assignmentSettings
    const assignmentSettings = {
        ...baseFormState.assignmentSettings,
        loginProtectionEnabled: values.controlAssignment.loginProtectionEnabled,
        segregationOfDutiesEnabled:
            values.controlAssignment.segregationOfDutiesEnabled,
        tagIds: { ...values.controlAssignment.tagIds },
    };

    return {
        ...baseFormState,
        title: values.title,
        description: values.description,
        controlProgramId: values.controlProgramId,
        tagIds: values.tagIds,
        scheduling,
        stepGroup,
        assignmentSettings,
        sendImmediately: values.sendImmediately,
        triggeredBy: values.triggeredBy,
    };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function parseHoursAndMinutes(sendTime: string) {
    const split = sendTime.split(":");
    return {
        hours: Number.parseInt(split[0], 10),
        minutes: Number.parseInt(split[1], 10),
    };
}

function toAllocatedTimesValues(
    formState: ControlViewFormState
): ControlViewFormSchema["allocatedTimes"] {
    const hoursAndMinutes = parseHoursAndMinutes(formState.scheduling.sendTime);
    const sendTime = moment({
        hours: hoursAndMinutes.hours,
        minutes: hoursAndMinutes.minutes,
    });

    const firstAllocatedTime =
        formState.stepGroup.subsequentSteps[0].allocatedTime;
    const secondAllocatedTime =
        formState.stepGroup.subsequentSteps[1].allocatedTime;

    const toSimple = (at: { days: number; microseconds: number }) =>
        at.microseconds === 0
            ? ({ value: at.days, unit: "days" } as const)
            : ({
                  value: microsToHours(at.microseconds),
                  unit: "hours",
              } as const);

    return {
        responsibleDays: formState.stepGroup.initialStep.allocatedTime.days,
        sendTime: sendTime.toDate(),
        firstReviewer: toSimple(firstAllocatedTime),
        secondReviewer: toSimple(secondAllocatedTime),
    };
}

function toControlAssignmentDataFromFormState(formState: ControlViewFormState) {
    return {
        initialAssignment: formState.stepGroup.initialStep.assignment,
        subsequentAssignments: formState.stepGroup.subsequentSteps.map(
            (m) => m.assignment
        ),
        loginProtectionEnabled:
            formState.assignmentSettings.loginProtectionEnabled,
        segregationOfDutiesEnabled:
            formState.assignmentSettings.segregationOfDutiesEnabled,
        tagIds: formState.assignmentSettings.tagIds,
        notificationRecipients: formState.stepGroup.notificationRecipients,
        entityIds: formState.stepGroup.entityIds,
    } satisfies ControlAssignmentData;
}

function reconstructStepGroup(
    values: ControlViewFormSchema,
    baseFormState: ControlViewFormState
): ControlViewFormState["stepGroup"] {
    const toStepAllocatedTime = (reviewer: {
        value: number;
        unit: "days" | "hours";
    }) => ({
        days: reviewer.unit === "days" ? reviewer.value : 0,
        microseconds:
            reviewer.unit === "hours" ? hoursToMicros(reviewer.value) : 0,
    });

    const initialStep = {
        ...baseFormState.stepGroup.initialStep,
        assignment: { ...values.controlAssignment.initialAssignment },
        allocatedTime: {
            days: values.allocatedTimes.responsibleDays,
            microseconds: 0,
        },
        fieldGroup: {
            ...baseFormState.stepGroup.initialStep.fieldGroup,
            fields: values.responsibleTasks as Field<
                InitialFieldType,
                string
            >[],
        },
    };

    const subsequentSteps = [
        {
            ...baseFormState.stepGroup.subsequentSteps[0],
            assignment: {
                ...values.controlAssignment.subsequentAssignments[0],
            },
            allocatedTime: toStepAllocatedTime(
                values.allocatedTimes.firstReviewer
            ),
            fieldGroup: {
                ...baseFormState.stepGroup.subsequentSteps[0].fieldGroup,
                fields: values.firstReviewerTasks as Field<
                    SubsequentFieldType,
                    string
                >[],
            },
        },
        {
            ...baseFormState.stepGroup.subsequentSteps[1],
            assignment: {
                ...values.controlAssignment.subsequentAssignments[1],
            },
            allocatedTime: toStepAllocatedTime(
                values.allocatedTimes.secondReviewer
            ),
            fieldGroup: {
                ...baseFormState.stepGroup.subsequentSteps[1].fieldGroup,
                fields: values.secondReviewerTasks as Field<
                    SubsequentFieldType,
                    string
                >[],
            },
        },
    ];

    return {
        id: baseFormState.stepGroup.id,
        initialStep,
        subsequentSteps,
        notificationRecipients: values.controlAssignment.notificationRecipients,
        entityIds: values.controlAssignment.entityIds,
    };
}
